import { useState } from "react";
import { useLocation } from "wouter";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLanguage } from "@/components/language-provider";
import { getApiUrl } from "@/lib/config";

const TEXTS = {
  pt: {
    title: "Excluir minha conta",
    desc: "Apaga a sua conta da FillSign. Você tem 7 dias para desfazer.",
    open: "Excluir minha conta",
    dialogTitle: "Excluir a sua conta?",
    what: "O que acontece:",
    steps: (email: string) => [
      "Você sai na hora e ninguém mais entra com esta conta. As chaves de API dela param de funcionar.",
      "A renovação do plano é cancelada: não há novas cobranças.",
      `Mandamos para ${email} um link que desfaz a exclusão. Ele vale por 7 dias.`,
      "Depois de 7 dias apagamos os dados da conta. Documentos já assinados ficam guardados como prova, como a lei pede: o PDF assinado e o registro das assinaturas não mudam.",
    ],
    typeLabel: "Para confirmar, digite EXCLUIR",
    word: "EXCLUIR",
    password: "Sua senha",
    reason: "Quer contar por que está saindo? (opcional)",
    reasonPlaceholder: "Isso ajuda a FillSign a melhorar.",
    cancel: "Voltar",
    confirm: "Excluir minha conta",
    failed: "Não deu certo agora. Tente de novo em alguns minutos.",
  },
  en: {
    title: "Delete my account",
    desc: "Deletes your FillSign account. You have 7 days to undo it.",
    open: "Delete my account",
    dialogTitle: "Delete your account?",
    what: "What happens:",
    steps: (email: string) => [
      "You are signed out at once and nobody can sign in with this account again. Its API keys stop working.",
      "The plan renewal is cancelled: no new charges.",
      `We send ${email} a link that undoes the deletion. It is valid for 7 days.`,
      "After 7 days we remove the account data. Documents already signed stay stored as evidence, as the law requires: the signed PDF and its signature record do not change.",
    ],
    typeLabel: "To confirm, type DELETE",
    word: "DELETE",
    password: "Your password",
    reason: "Want to tell us why you are leaving? (optional)",
    reasonPlaceholder: "It helps FillSign improve.",
    cancel: "Back",
    confirm: "Delete my account",
    failed: "That did not work now. Try again in a few minutes.",
  },
};

/** Profile: the danger zone. Locks the account at once; the e-mail link undoes it for 7 days. */
export function DeleteAccountCard({ email, hasPassword }: { email: string; hasPassword: boolean }) {
  const { language } = useLanguage();
  const lang = language === "en" ? "en" : "pt";
  const tx = TEXTS[lang];
  const [, setLocation] = useLocation();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [password, setPassword] = useState("");
  const [askPassword, setAskPassword] = useState(hasPassword);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirmed = typed.trim().toUpperCase() === tx.word || typed.trim().toUpperCase() === "EXCLUIR" || typed.trim().toUpperCase() === "DELETE";

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getApiUrl("/api/account/delete"), {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ confirm: typed.trim(), password: password || null, reason: reason.trim() || null, language: lang }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data?.code === "password") setAskPassword(true);
        setError(data?.message || tx.failed);
        return;
      }
      for (const k of ["token", "authToken", "user"]) localStorage.removeItem(k);
      setLocation(`/conta/excluida?ate=${encodeURIComponent(data?.scheduledFor ?? "")}`);
    } catch {
      setError(tx.failed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-destructive/40" data-testid="card-delete-account">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base text-destructive"><Trash2 className="h-4 w-4" />{tx.title}</CardTitle>
        <CardDescription>{tx.desc}</CardDescription>
      </CardHeader>
      <CardContent>
        <Button variant="outline" className="border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => { setOpen(true); setTyped(""); setPassword(""); setError(null); }} data-testid="button-open-delete-account">
          {tx.open}
        </Button>
      </CardContent>
      <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-destructive" />{tx.dialogTitle}</DialogTitle>
            <DialogDescription>{tx.what}</DialogDescription>
          </DialogHeader>
          <ol className="list-decimal space-y-1.5 pl-5 text-sm text-muted-foreground">
            {tx.steps(email).map((s) => <li key={s}>{s}</li>)}
          </ol>
          <div className="space-y-3 pt-2">
            <div className="space-y-1.5">
              <Label htmlFor="delete-confirm">{tx.typeLabel}</Label>
              <Input id="delete-confirm" autoComplete="off" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={tx.word} data-testid="input-delete-confirm" />
            </div>
            {askPassword && (
              <div className="space-y-1.5">
                <Label htmlFor="delete-password">{tx.password}</Label>
                <Input id="delete-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="input-delete-password" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="delete-reason">{tx.reason}</Label>
              <Textarea id="delete-reason" rows={2} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={tx.reasonPlaceholder} />
            </div>
            {error && <p className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-sm text-destructive" role="alert" data-testid="delete-account-error">{error}</p>}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" disabled={busy} onClick={() => setOpen(false)}>{tx.cancel}</Button>
            <Button variant="destructive" disabled={!confirmed || busy || (askPassword && !password)} onClick={() => void submit()} data-testid="button-confirm-delete-account">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{tx.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
